import {
  ContentCopy,
  FileUpload,
  MoveDown,
  MoveUp,
  RemoveCircleOutlined,
  SettingsSuggest,
  Upgrade,
} from '@mui/icons-material'
import { Delete, Edit, MoreHoriz } from '@workwave-tidal/core/icons'

import { preview } from '@/components/form/preview'

import { useCopyToOperationsButtonData } from './hooks/useCopyToOperationsButtonData'
import { useDuplicateButtonData } from './hooks/useDuplicateButtonData'
import { useEditOrderButtonData } from './hooks/useEditOrderButtonData'
import { useExportOrderButtonData } from './hooks/useExportOrderButtonData'
import { useFitInOrderButtonData } from './hooks/useFitInOrderButtonData'
import { useGoToPairedButtonData } from './hooks/useGoToPairedButtonData'
import { useUnassignButtonData } from './hooks/useUnassignButtonData'
import { useTexts } from './useTexts'
import { useResidentModalsApi } from '@/components/modals/ResidentModals'

interface Props {
  extOrder: uui.domain.client.rm.ExtendedOrderStep
  tab?: number
}
export function ConfigurationActions(props: Props) {
  const { extOrder, tab } = props
  const texts = useTexts()

  // Go to Paired Button Data
  const { hasPaired, goToPaired, goToPairedTooltip, goToPairedDisabled } =
    useGoToPairedButtonData(extOrder)

  // Copy to Operations Button Data
  const { isSimulation, copyToOperationsTooltip, copyToOperationsDisabled } =
    useCopyToOperationsButtonData()

  const {
    deleteOrders: { show: handleDeleteOrders },
    duplicateOrders: { show: handleDuplicateOrders },
    copySelectedOrders: { show: handleCopySelectedOrders },
  } = useResidentModalsApi()

  // Duplicate Button Data
  const { duplicateTooltip, duplicateDisabled } = useDuplicateButtonData()

  // Export Button Data
  const { exportOrder } = useExportOrderButtonData(extOrder)

  // Fit In Order Button Data
  const { fitInOrder, FitInOrderModal } = useFitInOrderButtonData()

  // Unassign Button Data
  const { unassignOrder, unassignOrderTooltip, unassignOrderDisabled, UnassignOrderModal } =
    useUnassignButtonData(extOrder)

  // Edit Button Data
  const { editOrder } = useEditOrderButtonData(extOrder)

  const { isUnassigned } = extOrder

  // Determine edit button tooltip based on tab
  const editTooltip = tab === 2 ? texts.editOrderDisabledInCommunication : texts.editOrder

  return (
    <>
      <preview.ActionBar
        testId="order-view-actions"
        limit={hasPaired ? 4 : 3}
        menuIcon={<MoreHoriz />}
        menuMode="textOnly"
        menuTooltip={texts.more}
      >
        <preview.ActionBarItem
          key="edit"
          icon={<Edit />}
          label={texts.editOrder}
          tooltip={editTooltip}
          onClick={editOrder}
          disabled={tab === 2}
          data-testid="orders__edit-button"
          data-trackid="orders__edit-button"
          disableWhenReadOnly
        />

        {hasPaired ? (
          <preview.ActionBarItem
            key="go-to-paired"
            icon={extOrder.orderStepType === 'd' ? <MoveUp /> : <MoveDown />}
            label={texts.goToPaired(extOrder.orderStepType)}
            onClick={goToPaired}
            tooltip={goToPairedTooltip}
            disabled={goToPairedDisabled}
            data-testid="order__form-gotopaired"
            data-trackid="order__form-gotopaired"
            disableWhenReadOnly={false}
          />
        ) : (
          <></>
        )}

        {!isUnassigned ? (
          <preview.ActionBarItem
            key="unassign"
            icon={<RemoveCircleOutlined />}
            label={texts.unassignOrder}
            tooltip={unassignOrderTooltip}
            onClick={unassignOrder}
            disabled={unassignOrderDisabled}
            data-testid="orders__unassign-button"
            data-trackid="orders__unassign-button"
            disableWhenReadOnly
          />
        ) : (
          <></>
        )}

        {isUnassigned ? (
          <preview.ActionBarItem
            key="fit-in"
            icon={<SettingsSuggest />}
            label={texts.fitInOrder}
            tooltip={texts.fitInOrder}
            onClick={fitInOrder}
            data-testid="orders__fit-in-button"
            data-trackid="orders__fit-in-button"
            disableWhenReadOnly
          />
        ) : (
          <></>
        )}

        <preview.ActionBarItem
          key="delete"
          icon={<Delete />}
          label={texts.deleteOrder}
          tooltip={texts.deleteOrder}
          onClick={() => handleDeleteOrders([extOrder.id])}
          data-testid="orders__delete-button"
          data-trackid="orders__delete-button"
          disableWhenReadOnly
        />

        <preview.ActionBarItem
          key="duplicate"
          icon={<ContentCopy />}
          label={texts.duplicate}
          onClick={() => handleDuplicateOrders([extOrder.id])}
          tooltip={duplicateTooltip}
          disabled={duplicateDisabled}
          className="pendo-order__form-duplicate"
          data-testid="order__form-duplicate"
          data-trackid="order__form-duplicate"
          disableWhenReadOnly
        />

        <preview.ActionBarItem
          key="export"
          icon={<FileUpload />}
          label={texts.exportOrder}
          onClick={exportOrder}
          tooltip={texts.exportOrder}
          data-testid="order__form-export"
          data-trackid="order__form-export"
          disableWhenReadOnly={false}
          divider={isSimulation}
        />

        {isSimulation ? (
          <preview.ActionBarItem
            key="copy-to-operations"
            icon={<Upgrade />}
            label={`${texts.copyToOperations}...`}
            onClick={() => handleCopySelectedOrders([extOrder.id])}
            tooltip={copyToOperationsTooltip}
            disabled={copyToOperationsDisabled}
            data-testid="orders-copy-to-operations"
            data-trackid="orders-copy-to-operations"
            disableWhenReadOnly
          />
        ) : (
          <></>
        )}
      </preview.ActionBar>

      <UnassignOrderModal orderStepIds={[extOrder.id]} />
      <FitInOrderModal orderStepIds={[extOrder.id]} />
    </>
  )
}
